// Copyright (c) Docugami, Inc. All rights reserved.

import { IconButton, IContextualMenuItem, Stack } from "@fluentui/react";
import { DgBlueColoredButtonProps, DgColoredButton } from "components/common/DgCommonStyledComponents";
import { dgFontFamily } from "components/common/Fonts";
import { SlidingFilterPanel } from "components/common/SlidingFilterPanel";
import { createFolderId } from "components/file_management/file_helpers";
import { appContentColor, backgroundGray, disabledDarkGreyText, emptyColor, homeGray, primaryTheme, themeBlue, ZIndices } from "components/shell/presentation/AppStyles";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { DgIconButton } from "components/shell/utils/DgIconButton";
import { IconRenderer } from "components/work_path/IconRenderer";
import { RequestState, useAsyncHelper } from "hooks/AsyncHelper";
import { useChunkSelection } from "hooks/chunkSelection";
import { StringPropertyKeys, useCollectionKeyFilter } from "hooks/collectionKeyFilter";
import { dgIconNames } from "hooks/dgIcons";
import { Property, useDocumentProperties } from "hooks/documentProperties";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { TokenRange } from "networking/api";
import { ReportEntryType } from "networking/reportsApi";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { folderIdSelector } from "store/dgml/dgmlSelectors";
import { useDeletePropertyMutation } from "store/frontend/frontendSlice";
import { selectLabelState } from "store/frontend/selectors";
import { createOperationId, useAddEntryMutation } from "store/reports/reportsSlice";
import { selectIsReportPublished, selectReportId, selectReportLabelIds } from "store/reports/selectors";
import { selectSelectedChunkInstance } from "store/sdvSelectors";
import styled, { css } from "styled-components";
import { DocumentPropertiesViewer } from "./documentPropertiesViewer";
import { PropertyCreator } from "./propertyCreator";
import { setSelectedLabel } from "components/reports/reportsActions";

const minPanelHeight = 220; // Minimum height of the properties panel

const CreatorContainer = styled.div`
    box-sizing: border-box;
    padding: 0px 5px;
    flex: 1;
`

const ContentBorderCss = css`
    border: 1px solid ${emptyColor};
`

const ContentItemContainer = styled.div<{ border?: boolean, flex?: number }>`
    display: flex;
    flex: ${props => props.flex !== undefined ? props.flex : 1} 1 auto;
    ${props => !!props.border && ContentBorderCss}
    overflow: auto;
    overflow-x: hidden;
    min-height: 0;
`

const ContentContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    gap: 5px;
    height: 100%;
    overflow: hidden;
`

const NewPropertyButton = styled(DgColoredButton)`
    margin-left: auto;
    margin-right: auto;
    display: flex;
    align-items: center;
    align-self: center;
    justify-content: center;
    color: ${primaryTheme.palette.white};
    font-size: 10px;
    gap: 5px;
    box-sizing: border-box;
    padding: 4px 8px;
    border-radius: 4px;
`

const DisabledCss = css`
    cursor: wait;
    * {
        opacity: .8;
        pointer-events: none;
    }
`

const ContentRoot = styled.div<{ disabled?: boolean; maxHeight?: number }>`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    width: 100%;
    gap: 5px;
    background: ${primaryTheme.palette.white};
    ${props => props.disabled && DisabledCss};
    padding: 10px 5px;
    box-sizing: border-box;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    ${props => props.maxHeight ? `max-height: ${props.maxHeight}px;` : ''}
`

const Header = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 35px;
    align-items: center;
    justify-content: center;
    user-select: none;
    background-color: ${backgroundGray};

    :hover {
        cursor: grab;
        :active {
            cursor: grabbing;
        }
    }
`;

const HeaderTitle = styled.span`
    font-family: ${dgFontFamily};
    font-size: 14px;
    line-height: 35px;
    font-weight: 500;
    text-align: center;
`

const PlaceholderTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 30px;
    place-self: center;
    width: 80%;
    height: 100%;
    justify-content: center;
    align-items: center;
`;

const PlaceholderText = styled.span`
    place-self: center;
    font-family: ${dgFontFamily};
    font-size: 14px;
    font-weight: 500;
    line-height: 17px;
    letter-spacing: 0em;
    text-align: center;
`;

const Root = styled.div<{ height?: number; autoHeight?: boolean }>`
    width: 350px;
    max-height: 90vh;
    position: absolute;
    overflow: hidden;
    border-radius: 10px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.18), 0 1.5px 6px rgba(0,0,0,0.10);
    border: 1px solid #e0e0e0;
    z-index: ${ZIndices.PropertiesPanel};
    background: ${primaryTheme.palette.white};
    height: ${props => props.autoHeight ? 'auto' : `${props.height}px`};
    max-height: 90vh;
`;

const ResizeHandle = styled.div`
    height: 8px;
    width: 100%;
    cursor: ns-resize;
    background: #f0f0f0;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    &::before {
        content: '';
        width: 40px;
        height: 4px;
        background: #ccc;
        border-radius: 2px;
    }
`;

const searchPropertiesList: StringPropertyKeys<Property>[] = ['label'];

interface DocumentPropertiesPanelProps {
    documentId: string;
    setPanelHidden?: (v: boolean) => void;
    hidden?: boolean;
}

export const DocumentPropertiesPanel = (props: DocumentPropertiesPanelProps): JSX.Element | null => {
    const folderId = folderIdSelector(props.documentId);
    const [isCreating, setIsCreating] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<Property | null>(null);
    const [saveError, setSaveError] = useState(false);
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [filterText, setFilterText] = useState<string | undefined>(undefined);
    const [panelHeight, setPanelHeight] = useState<number|undefined>(undefined);
    const [userResized, setUserResized] = useState(false);
    const [resizing, setResizing] = useState(false);

    const [panelPos, setPanelPos] = useState<{ x: number; y: number }>({ x: 100, y: 100 });
    const [dragging, setDragging] = useState(false);
    const dragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const rootRef = useRef<HTMLDivElement>(null);
    const startYRef = useRef(0);
    const startHeightRef = useRef(0);

    const reportId = useAppSelector(selectReportId);
    const reportLabelIds = useAppSelector(selectReportLabelIds);
    const labelState = useAppSelector(selectLabelState(folderId!.clusterId))
    const isPublishedReport = useAppSelector(selectIsReportPublished);

    const selectedChunkInstance = useAppSelector(selectSelectedChunkInstance);
    const { selectChunk, clearSelection } = useChunkSelection();
    const isExternalModalActive = useAppSelector(state => state.documents.modalSelectionContext.isActive);
    const { properties, isLoading, validate: isPropertyValid, addProperty } = useDocumentProperties(props.documentId);

    const [executeDeleteProperty] = useDeletePropertyMutation();

    const lifetimeRef = useRef(true);
    useEffect(() => () => { lifetimeRef.current = false }, []);

    const dispatch = useAppDispatch();

    const { execute: executeDelete, state: deleteState } = useAsyncHelper("Delete property");
    const { execute: executeSave, state: saveState } = useAsyncHelper("Save property");

    const selectedProperty = useMemo<Property | undefined>(() => {
        return properties.find(p => p.spanId === selectedChunkInstance?.spanId);
    }, [properties, selectedChunkInstance?.spanId]);

    const filteredProperties = useCollectionKeyFilter<Property>(filterText || '', properties, searchPropertiesList);

    const [addEntryTrigger, addEntryStatus] = useAddEntryMutation();

    useEffect(() => {
        if (saveState === RequestState.Error) {
            setSaveError(true);
        }
    }, [saveState]);

    useEffect(() => {
        // Reset editing/creating state when the panel is dismissed/hidden
        if (props.hidden) {
            setIsCreating(false);
        }
    }, [props.hidden])

    useEffect(() => {
        // Auto-dismiss the panel while navigating through documents
        props.setPanelHidden?.(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.documentId])

    useEffect(() => {
        if (!!selectedProperty) {
            props.setPanelHidden?.(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProperty]);

    const deleteProperty = async (property: Property) => {
        const folderId = createFolderId(props.documentId);
        await executeDeleteProperty({ docsetId: folderId.clusterId, propertyId: property.id });
        if (lifetimeRef.current) {
            if (property.spanId === selectedChunkInstance?.spanId) {
                clearSelection();
            }
        }
    }

    const createProperty = async (label: string, value: string, tokenRanges?: TokenRange[]) => {
        if (!isCreating) {
            return;
        }

        // Create
        const response = await addProperty(label, value, tokenRanges);
        if (lifetimeRef.current && !!response) {
            selectChunk(response.spanId);
            setIsCreating(false);
        }
    }

    function create() {
        clearSelection();
        setIsCreating(true);
    }

    function cancelEdit() {
        setIsCreating(false);
        clearSelection();
    }

    function canDrag(labelId: string): boolean {
        return !addEntryStatus.isLoading && !!labelState?.data?.entities[labelId] && !isExternalModalActive && !!labelId && !reportLabelIds.includes(labelId) && !isPublishedReport;
    }

    function onDragStart(labelId: string): void {
        if (canDrag(labelId)) {
            const label = labelState?.data?.entities[labelId]!;
            dispatch(setSelectedLabel(label.label, folderId!.clusterId))
        }
    }

    const getCreateNewPropertyButton = () => (
        <NewPropertyButton disabled={isCreating} onClick={create} controlButtonProps={DgBlueColoredButtonProps}>
            <IconRenderer
                source={{
                    name: dgIconNames.DgPlusBox,
                    height: 1,
                    width: .877,
                    defaultColor: primaryTheme.palette.white
                }}
                width={14}
                height={14} />
            CREATE A PROPERTY
        </NewPropertyButton>
    )

    const getPropertyCommands = (labelId: string): IContextualMenuItem[] => {
        const addToEntryDisabled = !canDrag(labelId);
        if (isPublishedReport) {
            return [];
        }

        return [{
            key: 'add_to_report',
            name: 'Add to report',
            disabled: addToEntryDisabled,
            iconProps: { iconName: 'CircleAddition', styles: { root: { color: addToEntryDisabled ? disabledDarkGreyText : appContentColor } } },
            onClick: () => { onAddEntry(labelId) },
        }]
    };

    const onAddEntry = async (labelId: string) => {
        const label = labelState?.data?.entities[labelId]?.label ?? null;
        if (!!label && !addEntryStatus.isLoading) {
            await addEntryTrigger({
                reportId: reportId,
                definition: {
                    title: label,
                    entryType: ReportEntryType.Text
                },
                operationId: createOperationId()
            });
        }
    };

    function getContent() {
        if (!isLoading && !properties.length && !isCreating) {
            return (
                <PlaceholderTextContainer>
                    <PlaceholderText>Properties allow you to add data to your documents. Click 'Create A Property’ to start.</PlaceholderText>
                    {getCreateNewPropertyButton()}
                </PlaceholderTextContainer>
            )
        }

        // Show the editor if we are editing a property, or creating a new one
        const content = isCreating ? (
            <ContentItemContainer>
                <CreatorContainer>
                    <PropertyCreator
                        onCancel={cancelEdit}
                        onSave={(label, value, tokenRanges) => executeSave(createProperty(label, value, tokenRanges))}
                        validate={isPropertyValid}
                        isSaving={saveState === RequestState.InProgress} />
                </CreatorContainer>
            </ContentItemContainer>
        ) : (
            <ContentItemContainer
                flex={1.75}
                onClick={() => clearSelection()}>
                <DocumentPropertiesViewer
                    properties={filteredProperties}
                    onSelect={p => selectChunk(p.spanId)}
                    onDelete={p => setPendingDelete(p)}
                    canDrag={canDrag}
                    onDragStart={onDragStart}
                    getCommands={getPropertyCommands}
                    isLoading={isLoading} />
            </ContentItemContainer>
        );

        return (
            <>
                <Stack horizontal styles={{ root: { position: 'relative' } }}>
                    {getCreateNewPropertyButton()}
                    {!isCreating && <div style={{ position: 'absolute', right: 0 }}>
                        <DgIconButton
                            iconName={dgIconNames.DgFilter}
                            onClick={() => setShowFilterPanel(current => !current)}
                            height={16}
                            width={16}
                            color={showFilterPanel ? themeBlue : homeGray} />
                    </div>}
                </Stack>
                {!isCreating && <Stack>
                    <SlidingFilterPanel
                        showFilterPanel={showFilterPanel}
                        filterText={filterText}
                        onFilterTextChange={setFilterText}
                        onClose={() => setShowFilterPanel(false)}
                        countText={`Showing ${filteredProperties.length} of ${properties.length} properties`}
                    />
                </Stack>}
                <ContentContainer>
                    {content}
                </ContentContainer>
            </>
        )
    }

    // Mouse event handlers for drag
    const onHeaderMouseDown = (e: React.MouseEvent) => {
        if (e.button !== 0) {
            return; // Only left click
        }

        setDragging(true);
        const rect = rootRef.current?.getBoundingClientRect();

        // Use pageX/pageY and subtract rect.left/top to get the offset within the panel
        dragOffset.current = {
            x: e.clientX - (rect?.left ?? 0),
            y: e.clientY - (rect?.top ?? 0),
        };
    };

    // Resize handlers
    const onResizeMouseDown = (e: React.MouseEvent) => {
        setResizing(true);
        setUserResized(true);
        startYRef.current = e.clientY;
        startHeightRef.current = panelHeight ?? rootRef.current?.offsetHeight ?? minPanelHeight;
        e.preventDefault();
        e.stopPropagation();
    };

    useEffect(() => {
        if (!dragging) {
            return;
        }

        const onMouseMove = (e: MouseEvent) => {
            const parentRect = rootRef.current?.offsetParent instanceof HTMLElement
                ? rootRef.current.offsetParent.getBoundingClientRect()
                : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };

            const panelRect = rootRef.current?.getBoundingClientRect();
            const panelWidth = panelRect?.width ?? 350;
            const panelHeight = panelRect?.height ?? 500;
            const parentWidth = parentRect.width ?? window.innerWidth;
            const parentHeight = parentRect.height ?? window.innerHeight;

            // Calculate unclamped position
            let x = e.clientX - dragOffset.current.x - parentRect.left;
            let y = e.clientY - dragOffset.current.y - parentRect.top;

            // Clamp so the panel stays within parent
            x = Math.max(0, Math.min(x, parentWidth - panelWidth));
            y = Math.max(0, Math.min(y, parentHeight - panelHeight));

            setPanelPos({ x, y });
        };

        const onMouseUp = () => {
            setDragging(false);
        };

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [dragging]);

    useEffect(() => {
        if (!resizing) return;
        const onMouseMove = (e: MouseEvent) => {
            const delta = e.clientY - startYRef.current;
            let newHeight = startHeightRef.current + delta;
            // Determine parent's bottom boundary
            const parentRect = rootRef.current?.offsetParent instanceof HTMLElement
                ? rootRef.current.offsetParent.getBoundingClientRect()
                : { top: 0, height: window.innerHeight };
            const panelRect = rootRef.current?.getBoundingClientRect();
            const panelTop = panelRect?.top ?? 0;
            const maxHeight = (parentRect.top + parentRect.height) - panelTop;
            setPanelHeight(Math.max(minPanelHeight, Math.min(maxHeight, newHeight)));
        };
        const onMouseUp = () => setResizing(false);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [resizing]);

    useEffect(() => {
        // Shrink panel if parent gets smaller and would clip the panel
        const handleResize = () => {
            if (!rootRef.current) return;
            const parentRect = rootRef.current.offsetParent instanceof HTMLElement
                ? rootRef.current.offsetParent.getBoundingClientRect()
                : { top: 0, height: window.innerHeight };
            const panelRect = rootRef.current.getBoundingClientRect();
            const panelTop = panelRect?.top ?? 0;
            const maxHeight = (parentRect.top + parentRect.height) - panelTop;
            if ((panelHeight ?? rootRef.current.offsetHeight) > maxHeight) {
                setPanelHeight(Math.max(minPanelHeight, maxHeight));
            }
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [panelHeight]);

    // If hidden, render nothing
    if (props.hidden) {
        return null;
    }

    return (
        <Root
            ref={rootRef}
            style={{ left: panelPos.x, top: panelPos.y }}
            height={userResized && !isCreating ? panelHeight : undefined}
            autoHeight={!userResized || isCreating}
        >
            <Header className='dragTarget' onMouseDown={onHeaderMouseDown}>
                <HeaderTitle>PROPERTIES</HeaderTitle>
                <IconButton
                    styles={{ root: { color: 'black', position: 'absolute', right: 0 } }}
                    iconProps={{ iconName: "ChromeClose", styles: { root: { fontSize: 10 } } }}
                    onClick={() => props.setPanelHidden?.(true)} />
            </Header>
            <ContentRoot
                disabled={saveState === RequestState.InProgress || deleteState === RequestState.InProgress}
                maxHeight={userResized && !isCreating && panelHeight !== undefined ? panelHeight - 35 - 8 : undefined}
            >
                {getContent()}
            </ContentRoot>
            {!isCreating && <ResizeHandle onMouseDown={onResizeMouseDown} />}
            <ConfirmationDialog
                hidden={!pendingDelete}
                title="Delete Property"
                subText="Are you sure you want to delete this property? This property will be removed for all documents in the doc set."
                confirmButtonText="Delete"
                dismissButtonText="Cancel"
                dismissedCallback={() => setPendingDelete(null)}
                confirmedCallback={() => {
                    executeDelete(deleteProperty(pendingDelete!));
                    setPendingDelete(null);
                }} />
            <ConfirmationDialog
                hidden={!saveError}
                title="Uh oh!"
                subText="Something went wrong when trying to save your property. Please try again later. If the problem persists, contact your Docugami representative."
                confirmButtonText="Ok"
                dismissedCallback={() => setSaveError(false)}
                confirmedCallback={() => setSaveError(false)} />
        </Root>
    );
}
